import { Service } from '../Service'
import { api } from '../api'
import { createParams } from '@/utils/dynamic'
import { setToModel } from '@/utils/objects'
import { ModelPaymentLog, FIELDS_CONFIG } from './models'
import { createDescription } from '@/utils/shared'
import { ServiceInvoice } from '@/services/Invoice'
import { ServiceError } from '@/services/Service'

const ENDPOINTS = {
  GET: 'Payments/Get',
  GET_DYNAMIC: 'Payments/GetAllDynamic',
  GET_ALL_BY_CUSTOMER: 'Payments/GetAllByCustomer',
  POST: 'Payments/Create',
  PATCH: 'Payments/Patch',
  PUT: 'Payments/Put',
  DELETE: 'Payments/Delete',
  GET_DYNAMIC_LOG: 'PaymentLogs/GetAllDynamic',
  CREATE_LOG: 'PaymentLogs/Create',
  DELETE_LOG: (id) => `PaymentLogs/Delete/${id}`
}

class ModuleServicePayments extends Service {
  async post (data) {
    const { isExist } = await this.checkExistPayment({ invoiceID: data.invoiceID, invoiceNumber: data.invoiceNumber, customerPO: data.customerPO })
    if (isExist) {
      throw new ServiceError('payment-already-exist')
    } else {
      return super.post(data)
    }
  }
  async createLog (data) {
    const _data = setToModel(new ModelPaymentLog(), data)
    delete _data['id']
    return api.post(`${ENDPOINTS.CREATE_LOG}`, _data)
  }
  async deleteLogsDynamic (params) {
    const _params = {
      ...params,
      select: 'id'
    }
    const { data: { value } } = await ServicePayments.getAllDynamicLogs(_params)

    let countSuccess = 0
    let countError = 0

    await Promise.all(value.map(({ id }) => {
      return ServicePayments
        .deleteLog(id)
        .then(() => countSuccess++)
        .catch(() => countError++)
    }))

    return {
      count: value.length,
      countSuccess,
      countError
    }
  }
  async getAllDynamicLogs (params) {
    const _params = createParams(params)

    return api
      .get(ENDPOINTS.GET_DYNAMIC_LOG, { params: _params })
  }
  async deleteLog (id) {
    return api.delete(ENDPOINTS.DELETE_LOG(id))
  }
  async getAllByCustomer (customerID) {
    if (!customerID) {
      console.log('customerID is required')
      throw new Error('Client Error')
    }
    return api.get(`${ENDPOINTS.GET_ALL_BY_CUSTOMER}/${customerID}`)
  }
  async sendReminder ({ customerID, logDescription }) {
    const { data } = await this.getAllByCustomer(customerID)
    return Promise.all(data.map((paymentData) => {
      return this.createLog({ description: logDescription, paymentID: paymentData.id })
    }))
  }
  async updateNotification ({ field, id, labelSelect, value }) {
    const config = FIELDS_CONFIG[field]

    if (!config) {
      return Promise.reject(new Error(`Config is empty`))
    }

    const { label, type } = config

    const description = createDescription({ type, label, labelSelect, value })

    return this.createLog({ paymentID: id, description })
  }
  async checkExistPayment ({ invoiceID, customerPO, invoiceNumber }) {
    if (!invoiceID) {
      console.log('invoiceID is required')
      throw new ServiceError('Client Error')
    }
    return this.getAllDynamic({
      filter: [
        `invoiceID == ${invoiceID}`,
        `customerPO == "${customerPO}"`,
        `invoiceNumber == "${invoiceNumber}"`
      ].join(' && ')
    })
      .then(({ data }) => {
        if (data.value.length === 0) {
          return { isExist: false, data: null }
        }
        return { isExist: true, data: data.value[0] }
      })
  }
  async getPaymentDataByInvoice ({ invoiceID }) {
    const { data: invoiceData } = await ServiceInvoice.getDynamic(invoiceID, {
      select: [
        'invoiceNumber', 'rfq.customerCompany.id as companyID', 'po as customerPO', 'invoiceDate', 'dueDate as invoiceDueDate',
        'vat.vatValue as vatValue', 'rfqid', 'bankFees', 'aogCharges', 'shippingCharges'
      ].join(',')
    })
    const { rfqid, vatValue, bankFees, aogCharges, shippingCharges, ...restInvoiceData } = invoiceData
    const { invoiceNumber } = invoiceData

    const { data: dataRFQ3s } = await ServiceInvoice
      .getInvoiceDetailsRFQ3({ invoiceNumber, rfqid, params: { select: 'currencyID,price,qty' } })

    const invoiceAmountInit = Number(bankFees || 0) + Number(aogCharges || 0) + Number(shippingCharges || 0)
    const invoiceAmount = dataRFQ3s.value.reduce((sum, { price, qty }) => sum + (price * qty), invoiceAmountInit || 0)

    const currencyID = (dataRFQ3s.value[0] && dataRFQ3s.value[0].currencyID) || null
    const invoiceVATAmount = invoiceAmount * (vatValue || 0) / 100

    const _invoiceAmount = invoiceAmount + invoiceVATAmount

    return { data: { ...restInvoiceData, currencyID, invoiceAmount: _invoiceAmount, invoiceVATAmount, invoiceID } }
  }
}

export const ServicePayments = new ModuleServicePayments({
  apiGet: ENDPOINTS.GET,
  apiGetDynamic: ENDPOINTS.GET_DYNAMIC,
  apiPost: ENDPOINTS.POST,
  apiPatch: ENDPOINTS.PATCH,
  apiPut: ENDPOINTS.PUT,
  apiDelete: ENDPOINTS.DELETE
})

export * from './models'
